import {
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  Output,
  AfterViewInit,
  OnDestroy,
  ViewContainerRef,
  ViewChild,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ModalConfig, ModalService } from '../../services/modal.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer,SafeResourceUrl  } from '@angular/platform-browser';
import { SharedCommonService } from '../../services/shared-common.service';

@Component({
  selector: 'app-modal',
  standalone: true, // Add this to make the component standalone
  imports: [CommonModule, TranslateModule, FormsModule],
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
})
export class ModalComponent implements AfterViewInit, OnDestroy {
  @Input() modalId!: string;
  @Output() closed = new EventEmitter<void>();
  @Output() opened = new EventEmitter<void>();
  public readonly translate = inject(TranslateService);
  public readonly sanitizer = inject(DomSanitizer);
  private readonly sharedCommonService = inject(SharedCommonService);
  contractSafeUrl: SafeResourceUrl | null = null;
  iframeLoaded: boolean = false;
  config?: ModalConfig;
  isOpen = false;
  note: string = '';
  noteError: boolean = false;
  attachment: File | null = null;
  attachmentName: string = '';
  attachmentTooLarge: boolean = false;
  private readonly maxAttachmentSizeBytes = 20 * 1024 * 1024;
  private subscription?: Subscription;
  private componentRef?: ComponentRef<any>;
   @ViewChild("dynamicComponentContainer", { read: ViewContainerRef, static: false })
  dynamicComponentContainer!: ViewContainerRef;
  constructor(
    private readonly modalService: ModalService,
  ) {}

  ngAfterViewInit() {
    this.subscription = this.modalService.modals$.subscribe((modals) => {
      const modal = modals.find((m) => m.id === this.modalId);
      const wasOpen = this.isOpen;

      this.config = modal;
      this.isOpen = !!modal; // Set isOpen to true if the modal is present
      
      if (this.isOpen && !wasOpen) {
        this.opened.emit();
        // Use setTimeout to ensure the DOM is fully rendered and avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
          this.loadDynamicComponent();
        }, 0);
      } else if (!this.isOpen && wasOpen) {
        this.destroyDynamicComponent();
        this.closed.emit();
        this.resetNoteState();
      }
    });
  }
  private resetNoteState(): void {
  this.note = '';
  this.noteError = false;
  this.attachment = null;
  this.attachmentName = '';
  this.attachmentTooLarge = false;
}

  ngOnDestroy() {

    this.subscription?.unsubscribe();
    this.destroyDynamicComponent();
  }

  closeModal() {
    // Check if there's a close callback for this modal
    if (this.config?.data?.onClose) {
      console.log('Modal: Calling onClose callback for modalId:', this.modalId);
      this.config.data.onClose();
      return; // Don't call modalService.closeModal as the callback should handle it
    }
    
    // Default behavior
    this.modalService.closeModal(this.modalId);
  }  onBackdropClick(event: Event) {
    event.stopPropagation();
    this.modalService.handleBackdropClick(this.modalId);
  }

  private loadDynamicComponent() {
    if (!this.config?.component) {
      console.warn('No component configured for modalId:', this.modalId);
      return;
    }

    if (!this.dynamicComponentContainer) {
      console.warn('dynamicComponentContainer not available for modalId:', this.modalId);
      // Retry after a short delay to allow ViewChild to initialize
      setTimeout(() => {
        if (this.dynamicComponentContainer && this.config?.component) {
          this.loadDynamicComponent();
        } else {
          console.error('dynamicComponentContainer still not available after retry for modalId:', this.modalId);
        }
      }, 10);
      return;
    }

    try {
      // Clear any previous components before creating a new one
      this.destroyDynamicComponent();

      // Create the dynamic component
      this.componentRef = this.dynamicComponentContainer.createComponent(this.config.component);
      this.componentRef.instance.config = this.config;
      
      // Pass data to the dynamically loaded component if applicable
      if (this.config.data && 'data' in this.componentRef.instance) {
        this.componentRef.instance.data = this.config.data;
      }
      
    } catch (error) {
      console.error('Error loading dynamic component for modalId:', this.modalId, error);
    }
  }

  private destroyDynamicComponent() {
    if (this.componentRef) {
      this.componentRef.destroy();
      this.componentRef = undefined;
    }
    if (this.dynamicComponentContainer) {
      this.dynamicComponentContainer.clear();
    }
  }

  get modalSizeClasses(): string {
    const sizeMap = {
      sm: 'w-full max-w-md',
      md: 'w-full max-w-2xl',
      lg: 'w-full max-w-5xl',
      xl: 'w-full max-w-7xl',
      full: 'w-full max-w-[95vw]',
    };
    return sizeMap[this.config?.size || 'lg'];
  }

  

  // Confirmation dialog specific properties and methods
  get isConfirmationDialog(): boolean {
    return this.modalId === 'confirmation-dialog';
  }

   get confirmationButtonClasses(): string {
    const type = this.config?.data?.type || 'primary ';
     return type === 'danger' ? 'bg-red-600 hover:bg-red-700' : ' bg-[#854AEA]';
  }

  onConfirmationConfirm(): void {
    if (this.config?.data?.requireNote && !this.note.trim()) {
    this.noteError = true;
    return;
  }

  if (this.config?.data?.onConfirm) {
    this.config.data.onConfirm(this.note, this.attachment); // Pass note and attachment back
  }
  }

  onAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      if (file.size > this.maxAttachmentSizeBytes) {
        this.attachmentTooLarge = true;
        this.attachment = null;
        this.attachmentName = '';
        input.value = '';
        this.sharedCommonService.showToast('danger', this.translate.instant('COMMON.ATTACHMENT_TOO_LARGE'));
        return;
      }

      this.attachmentTooLarge = false;
      this.attachment = file;
      this.attachmentName = this.attachment.name;
    }
  }

  removeAttachment(): void {
    this.attachment = null;
    this.attachmentName = '';
    this.attachmentTooLarge = false;
  }

  downloadLocalAttachment(): void {
    if (!this.attachment) return;
    
    const blob = new Blob([this.attachment], { type: this.attachment.type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = this.attachment.name;
    link.click();
    URL.revokeObjectURL(url);
  }

  getFileIcon(fileName: string): string {
    if (!fileName) return 'icons/pdf-icon-grey.svg';
    
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'icons/pdf.svg';
      case 'doc':
      case 'docx':
        return 'icons/word.svg';
      case 'xls':
      case 'xlsx':
        return 'icons/excel.svg';
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'svg':
        return 'icons/jpg.svg';
      case 'zip':
      case 'rar':
        return 'icons/zip-icon.svg';
      default:
        return 'icons/pdf-icon-grey.svg';
    }
  }

  onConfirmationCancel(): void {
    if (this.config?.data?.onCancel) {
    this.config.data.onCancel();
  }
  }

   onIframeLoad(): void {
    if (!this.iframeLoaded) {
      // Set the flag to true to prevent reloading the iframe
      this.contractSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.config?.link || '');
      this.iframeLoaded = true;
      console.log('Iframe has loaded successfully.');
    } else {
      console.log('Iframe already loaded, not updating URL again.');
    }
  }
}
